import React, { useEffect, useRef, useState } from "react";
import { X, Microphone, Stop, MagicWand, SpeakerHigh, Pause, Waveform } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import VoiceEffectsPanel from "./VoiceEffectsPanel";
import { installVoiceEnhancer } from "../lib/voiceEnhancer";

const MAX_SEC = 10;

/**
 * VoiceReactionsSheet — reply to a post's Voice Drop with your own effect
 * voice (Robot, KTV & friends via Voice FX). Lists the reaction chain.
 */
export default function VoiceReactionsSheet({ post, open, onClose }) {
  const [reactions, setReactions] = useState([]);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [pending, setPending] = useState(null); // { url, blob, seconds }
  const [saving, setSaving] = useState(false);
  const [showFx, setShowFx] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const secondsRef = useRef(0);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!open || !post?.id) return;
    api.get(`/posts/${post.id}/voice-reactions`)
      .then(({ data }) => setReactions(data || []))
      .catch(() => {});
  }, [open, post?.id]);

  useEffect(() => () => {
    stopStream();
    if (audioRef.current) audioRef.current.pause();
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      if (streamRef.current.__rawStream) {
        streamRef.current.__rawStream.getTracks().forEach((t) => t.stop());
      }
      streamRef.current = null;
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const startRec = async () => {
    try {
      installVoiceEnhancer();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        setPending((prev) => {
          if (prev?.url) URL.revokeObjectURL(prev.url);
          return { url: URL.createObjectURL(blob), blob, seconds: secondsRef.current };
        });
        stopStream();
        setRecording(false);
      };
      rec.start();
      recRef.current = rec;
      secondsRef.current = 0;
      setSeconds(0);
      timerRef.current = setInterval(() => {
        secondsRef.current += 1;
        setSeconds(secondsRef.current);
        if (secondsRef.current >= MAX_SEC) stopRec();
      }, 1000);
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission needed.");
    }
  };

  const stopRec = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  };

  const send = async () => {
    if (!pending?.blob) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", pending.blob, "reaction.webm");
      fd.append("title", "Voice Reaction");
      fd.append("kind", "recording");
      fd.append("is_public", "false");
      fd.append("duration_sec", String(pending.seconds || 0));
      const { data: up } = await api.post("/voice/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data: rx } = await api.post(`/posts/${post.id}/voice-reactions`, {
        voice_path: up.storage_path,
        duration: pending.seconds || null,
      });
      setReactions((r) => [rx, ...r]);
      URL.revokeObjectURL(pending.url);
      setPending(null);
      toast.success("Voice reaction sent!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reaction");
    } finally {
      setSaving(false);
    }
  };

  const togglePlay = (r) => {
    if (playingId === r.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(fileUrl(r.voice_path));
    a.onended = () => setPlayingId(null);
    a.play().catch(() => {});
    audioRef.current = a;
    setPlayingId(r.id);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[115] flex items-end justify-center" data-testid="voice-reactions-sheet">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[75dvh] overflow-y-auto bg-ink border-t border-white/15 rounded-t-3xl p-5 pb-8">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Waveform size={18} className="text-papaya" weight="fill" />
            <p className="text-cream font-black uppercase tracking-[0.2em] text-xs">Voice Reactions</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 text-cream flex items-center justify-center" data-testid="voice-reactions-close">
            <X size={16} />
          </button>
        </div>
        <p className="text-cream/50 text-xs mb-4">Reply with your own effect voice — Robot, KTV, Monster & more.</p>

        {/* Recorder */}
        {!pending ? (
          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 mb-4">
            {!recording ? (
              <button
                onClick={startRec}
                data-testid="reaction-record-btn"
                className="w-10 h-10 rounded-full bg-papaya text-ink flex items-center justify-center hover:brightness-110 flex-shrink-0"
              >
                <Microphone size={18} weight="fill" />
              </button>
            ) : (
              <button
                onClick={stopRec}
                data-testid="reaction-stop-btn"
                className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center animate-pulse flex-shrink-0"
              >
                <Stop size={18} weight="fill" />
              </button>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-cream font-bold text-sm">
                {recording ? `Recording… ${seconds}s / ${MAX_SEC}s` : "Record your reaction"}
              </p>
              <p className="text-cream/50 text-[11px]">Pick an effect first with Voice FX</p>
            </div>
            <button
              onClick={() => setShowFx(true)}
              data-testid="reaction-voice-fx"
              className="flex items-center gap-1 text-xs font-bold text-papaya hover:text-papaya-hover flex-shrink-0"
            >
              <MagicWand size={14} weight="fill" /> FX
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-papaya/10 border border-papaya/40 mb-4" data-testid="reaction-preview">
            <audio src={pending.url} controls className="flex-1 h-9" />
            <button
              onClick={send}
              disabled={saving}
              data-testid="reaction-send-btn"
              className="rounded-full bg-papaya text-ink px-4 py-2 text-[10px] font-black tracking-[0.15em] uppercase disabled:opacity-50 flex-shrink-0"
            >
              {saving ? "Sending…" : "Send"}
            </button>
            <button
              onClick={() => { URL.revokeObjectURL(pending.url); setPending(null); }}
              data-testid="reaction-discard-btn"
              className="text-cream/50 hover:text-cream flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Chain */}
        {reactions.length === 0 ? (
          <p className="text-cream/40 text-xs" data-testid="reactions-empty">No reactions yet — start the chain!</p>
        ) : (
          <div className="space-y-2" data-testid="reactions-list">
            {reactions.map((r) => (
              <div key={r.id} className="flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 px-3 py-2">
                <button
                  onClick={() => togglePlay(r)}
                  data-testid={`reaction-play-${r.id}`}
                  className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    playingId === r.id ? "bg-papaya text-ink animate-pulse" : "bg-papaya/20 text-papaya"
                  }`}
                >
                  {playingId === r.id ? <Pause size={15} weight="fill" /> : <SpeakerHigh size={15} weight="fill" />}
                </button>
                {r.avatar_path && (
                  <img src={fileUrl(r.avatar_path)} alt="" className="w-6 h-6 rounded-full object-cover" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-cream text-xs font-bold truncate">@{r.username}</p>
                  <p className="text-cream/40 text-[10px]">{r.duration ? `${Math.round(r.duration)}s` : "voice reaction"}</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const { data: snd } = await api.post("/music/from-reaction", { reaction_id: r.id });
                      toast.success(snd.already_existed
                        ? "Already a sound — find it in the music picker 🎧"
                        : "Remix sound created — use it on your next video 🎧");
                    } catch (err) {
                      toast.error("Could not create remix sound");
                    }
                  }}
                  data-testid={`reaction-remix-${r.id}`}
                  className="rounded-full bg-white/10 text-cream/70 px-2.5 py-1.5 text-[9px] font-black tracking-[0.15em] uppercase hover:bg-white/20 flex-shrink-0"
                >
                  Remix
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <VoiceEffectsPanel open={showFx} onClose={() => setShowFx(false)} />
    </div>
  );
}
